import { createColumnHelper } from "@tanstack/react-table";
import classNames from "classnames";
import moment from "moment-timezone";
import { Option } from "rc-select";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import ResourceSelect from "../../../components/Inputs/ResourceSelect";
import Select from "../../../components/Inputs/Select";
import { dateTimeFormat } from "../../../utils/helpers/date";
import {
	getSettingsOrgResourceSearchFilters,
	getSettingsResourceSearchFilters,
	renderSettingsResourceLabel,
} from "../../../utils/helpers/settings";
import { useCompanyDateFormat, useCompanyTimeFormat } from "../../../utils/hooks/company";
import { useGroups } from "../../Settings/General/OrganisationLevelGroups/context";
import StatusCell from "./StatusCell";
import { useOrgSettingsResourceLabel } from "../../../utils/hooks/useOrgSettingsResourceLabel";

const columnHelper = createColumnHelper();

export const useColumns = ({ edit, remove, view, cancel, timeoff, namespace }) => {
	const { t } = useTranslation();
	const dateFormat = useCompanyDateFormat();
	const timeFormat = useCompanyTimeFormat();
	const { groups } = useGroups();
	const renderOrgLevelResourceLabel = useOrgSettingsResourceLabel();

	const columns = useMemo(() => {
		const cols = [];

		if (namespace === "timesheet") {
			cols.push(
				columnHelper.accessor("date", {
					enableColumnFilter: true,
					header: t("date"),
					cell: ({ row: { original } }) => {
						const date = original?.data?.date || original?.data?.startDate;
						if (!date) {
							return null;
						}
						return moment(date).format(dateFormat);
					}
				}),
			);
		}

		if (namespace === "pto") {
			cols.push(
				columnHelper.accessor("data.allDay", {
					enableColumnFilter: true,
					header: t("all-day"),
					cell: (info) => info.getValue() ? t("yes") : t("no"),
				}),
			);
		}

		if (namespace !== "availability") {
			cols.push(
				columnHelper.accessor("startDate", {
					enableColumnFilter: true,
					header: t("start-time"),
					cell: ({ row: { original } }) => {
						if (original.type === "meal-waive") {
							return null;
						}
						const startDate = original.data?.startDate;
						if (!startDate) {
							return null;
						}
						if (namespace === "pto") {
							const { allDay } = original.data;
							if (allDay) {
								return dateTimeFormat(startDate, dateFormat);
							}
						}
						return `${dateTimeFormat(startDate, dateFormat)} ${dateTimeFormat(startDate, timeFormat)}`;
					}
				}),
				columnHelper.accessor("endDate", {
					enableColumnFilter: true,
					header: t("end-time"),
					cell: ({ row: { original } }) => {
						if (original.type === "meal-waive") {
							return null;
						}
						const endDate = original.data?.endDate;
						if (!endDate) {
							return null;
						}
						if (namespace === "pto") {
							const { allDay } = original.data;
							if (allDay) {
								return dateTimeFormat(endDate, dateFormat);
							}
						}
						return `${dateTimeFormat(endDate, dateFormat)} ${dateTimeFormat(endDate, timeFormat)}`;
					}
				}),
			);
		}

		if (namespace === "pto") {
			const startDateIndex = cols.findIndex(col => col.accessorKey === "data.allDay");
			cols.splice(
				startDateIndex,
				0,
				columnHelper.accessor("type", {
					enableColumnFilter: true,
					header: t("plan"),
					cell: ({ row }) => {
						const plan = row.original?.data?.plan?.plan;
						return plan && renderSettingsResourceLabel(plan);
					},
					filterType: "resource-select",
					Filter: (
						<ResourceSelect
							labelPropName="description"
							resourcePath="/timeoff"
							mode="multiple"
							renderLabel={renderSettingsResourceLabel}
							getSearchFilters={getSettingsResourceSearchFilters}
							hasSearch
						/>
					),
				}),
			)
		} else {
			cols.push(
				columnHelper.accessor("type", {
					header: t("type"),
					cell: (info) => t(info.getValue())
				})
			);
		}

		if (namespace !== "pto") {
			cols.push(
				columnHelper.accessor("data.location", {
					enableColumnFilter: true,
					header: t("location"),
					cell: (info) => {
						const value = info.getValue();
						return value && renderSettingsResourceLabel(value);
					},
					filterType: "resource-select",
					Filter: (
						<ResourceSelect
							labelPropName="description"
							resourcePath="/locations"
							mode="multiple"
							renderLabel={renderSettingsResourceLabel}
							getSearchFilters={getSettingsResourceSearchFilters}
							hasSearch
						/>
					),
				})
			);
			cols.push(
				columnHelper.accessor("data.job", {
					enableColumnFilter: true,
					header: t("job"),
					cell: (info) => {
						const value = info.getValue();
						return value && renderSettingsResourceLabel(value);
					},
					filterType: "resource-select",
					Filter: (
						<ResourceSelect
							labelPropName="description"
							resourcePath="/jobs"
							mode="multiple"
							renderLabel={renderSettingsResourceLabel}
							getSearchFilters={getSettingsResourceSearchFilters}
							hasSearch
						/>
					),
				})
			);
		}

		if (groups) {
			const groupColumns = groups.map((group) => {
				const level = `${group.level}`.replace("_", "");
				return columnHelper.accessor(level, {
					enableColumnFilter: true,
					header: group.description,
					cell: (info) => {
						const value = info.getValue();
						if (value && group.allocatedBy) {
							return renderOrgLevelResourceLabel(value);
						}
						return renderSettingsResourceLabel(value);
					},
					filterType: "resource-select",
					Filter: (
						<ResourceSelect
							labelPropName="description"
							resourcePath={`/${group?.id}/org-levels`}
							mode="multiple"
							renderLabel={renderOrgLevelResourceLabel}
							getSearchFilters={
								getSettingsOrgResourceSearchFilters
							}
							valuePropName="id"
							hasSearch
						/>
					),
				});
			});
			cols.push(...groupColumns);
		}

		cols.push(
			columnHelper.accessor("status", {
				enableColumnFilter: true,
				header: t("status"),
				cell: ({ row }) => <StatusCell value={row.original} />,
				filterType: "select",
				Filter: (
					<Select>
						<Option value="approved">{t("approved")}</Option>
						<Option value="denied">{t("denied")}</Option>
						<Option value="pending">{t("pending")}</Option>
						<Option value="canceled">{t("canceled")}</Option>
						<Option value="expired">{t("expired")}</Option>
					</Select>
				),
			})
		);

		if (edit || remove || cancel || view) {
			const actionsColumn = columnHelper.display({
				id: "actions",
				enableHiding: false,
				enableColumnFilter: false,
				header: () => (
					<div className="text-right">
						{t("actions")}
					</div>
				),
				cell: ({ row: { original } }) => {
					const viewAction = typeof view === "function" ? view(original) : null;
					const editAction = typeof edit === "function" ? edit(original) : null;
					const removeAction = typeof remove === "function" ? remove(original) : null;
					const cancelAction = typeof cancel === "function" ? cancel(original) : null;

					if (
						original?.status === "canceled"
						|| (original?.status === "approved" && original?.type === "cancel")
					) {
						return null;
					}

					return (
						<div
							className={classNames(
								"d-flex justify-content-end",
							)}
						>
							{viewAction && (
								<span
									className="fa-solid fa-eye mr-3 pb-2 text-primary cursor-pointer"
									onClick={viewAction}
									style={{ fontSize: "17px" }}
								/>
							)}

							{
								editAction
								&& original.status === "pending"
								&& !["submit-schedule", "cancel", "swap-shifts", "offer-shifts", "availability", "meal-waive"].includes(original?.type)
								&& (
									<span
										className="btn-inner--icon icon-sm cursor-pointer text-primary"
										onClick={editAction}
									>
										<i className="fas fa-pen" />
									</span>
								)}

							{removeAction && original.status === "pending" && original?.type !== "submit-schedule" && (
								<span
									className="btn-inner--icon mr-3 icon-sm cursor-pointer text-primary"
									onClick={removeAction}
								>
									<i className="fas fa-trash" />
								</span>
							)}

							{cancelAction && original.status === "approved" && (original?.type === "absence" || original?.type === "meal-waive") && (
								<span
									className="btn-inner--icon mr-3 icon-sm cursor-pointer text-primary"
									onClick={cancelAction}
									title={t("cancel-request")}
								>
									<i className="fas fa-ban" />
								</span>
							)}
						</div>
					);
				}
			});
			cols.push(actionsColumn);
		}

		return cols;
	}, [timeoff, t, namespace, edit, remove, cancel, groups, dateFormat, timeFormat, view, renderOrgLevelResourceLabel]);

	return columns;
};
